/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { type PropsWithChildren } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	tableWrapper: {
		boxSizing: 'border-box',
		width: '100%',
		// do not shrink if the issues table is large, just let the table extend past the bottom of the page
		flexShrink: 0,
		display: 'flex',
		flexDirection: 'column',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
	},
	tableWrapperFullHeight: {
		// issues table should expand to take up the container (minus the bottom padding) when the projects table has no results
		maxHeight: `calc(100% - ${token('space.300')})`,
	},
	tableWrapperRestrictedHeight: {
		// issues table should only take up a max of 75% of the container height when the projects table is also shown
		maxHeight: '75%',
	},
	tableScrollWrapper: {
		// this inner wrapper scrolls horizontally (and vertically) while the footer stays pinned outside it
		overflowX: 'auto',
		overflowY: 'auto',
		flex: 1,
		minHeight: 0,
		// hide scrollbars by default, show them on hover so users know they can scroll
		scrollbarWidth: 'none',
		'-ms-overflow-style': 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&::-webkit-scrollbar': {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:hover': {
			// firefox
			scrollbarWidth: 'thin',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:hover::-webkit-scrollbar': {
			display: 'block',
			width: '6px',
			height: '6px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&::-webkit-scrollbar-track': {
			background: 'transparent',
			boxShadow: 'none',
			border: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: token('color.background.neutral.bold'),
			borderRadius: token('radius.full'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&::-webkit-scrollbar-corner': {
			background: 'transparent',
		},
	},
	table: {
		display: 'table',
		// table will respect the fixed width of the first column and make the other columns responsive
		tableLayout: 'fixed',
		width: '100%',
		height: '100%',
		// allow explicit control of borders within the table
		borderCollapse: 'separate',
		borderSpacing: '0px',
		borderWidth: '0px',
	},
	thead: {
		display: 'table-header-group',
		// header will float while the user scrolls
		position: 'sticky',
		top: 0,
		zIndex: 1,
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	headerRow: {
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface.sunken'),

		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> th:first-of-type': {
			// there is a top-level style that targets the first and last cell of any table rows
			// that removes padding so we need to override this by using a descendant selector
			paddingLeft: token('space.100'),

			// the left border is provided by the table's outer container
			borderLeftWidth: '0px',
			borderTopLeftRadius: token('radius.small'),
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> th:last-of-type': {
			// there is a top-level style that targets the first and last cell of any table rows
			// that removes padding so we need to override this by using a descendant selector
			paddingRight: token('space.100'),

			// the right border is provided by the table's outer container
			borderRightWidth: '0px',
			borderTopRightRadius: token('radius.small'),
		},
	},
	thSpace: {
		display: 'table-cell',
		verticalAlign: 'middle',
		textAlign: 'left',
		whiteSpace: 'nowrap',

		// fixed-width breakpoints taken from Figma: https://www.figma.com/design/D6160TfMfREv8jBeAsw5ma/Blend-Search---Chat?node-id=2419-121123&t=56eOb7RsXea00pZJ-4
		'@media (min-width: 0px)': {
			width: '210px',
		},
		'@media (min-width: 1026px)': {
			width: '317px',
		},
		'@media (min-width: 1440px)': {
			width: '362px',
		},
	},
	thKey: {
		'@media (min-width: 0px)': {
			width: '80px',
		},
	},
	thType: {
		'@media (min-width: 0px)': {
			width: '140px',
		},
		'@media (min-width: 1026px)': {
			width: '180px',
		},
	},
	thLead: {
		'@media (min-width: 0px)': {
			width: '120px',
		},
		'@media (min-width: 768px)': {
			width: '140px',
		},
		'@media (min-width: 1026px)': {
			width: '172px',
		},
	},
	thCategory: {
		'@media (min-width: 0px)': {
			width: '80px',
		},
		'@media (min-width: 1026px)': {
			width: '116px',
		},
	},
	thSpaceUrl: {
		'@media (min-width: 0px)': {
			width: '80px',
		},
		'@media (min-width: 1026px)': {
			width: '112px',
		},
	},
	thBase: {
		display: 'table-cell',
		boxSizing: 'border-box',
		verticalAlign: 'middle',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		overflowX: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text.subtlest'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		// every table cell (except the first and last) will have a right and bottom border
		borderRightWidth: token('border.width'),
		borderBottomWidth: token('border.width'),
		borderTopWidth: '0px',
		borderLeftWidth: '0px',
	},
	tbody: {
		display: 'table-row-group',

		// remove the bottom border from all cells in the last row in the
		// table because this border will be provided by the footer
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& > tr:last-of-type > td': {
			borderBottomWidth: '0px',
		},
	},
	footer: {
		// footer is outside the scrollable area so it is always visible
		flexShrink: 0,
		boxSizing: 'border-box',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		font: token('font.body'),
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		borderBottomLeftRadius: token('radius.small'),
		borderBottomRightRadius: token('radius.small'),
	},
});

const TableHeader = () => {
	const { formatMessage } = useIntl();

	return (
		<thead css={styles.thead}>
			<tr css={styles.headerRow}>
				<th css={[styles.thBase, styles.thSpace]}>{formatMessage(messages.spacesColumnHeader)}</th>
				<th css={[styles.thBase, styles.thKey]}>{formatMessage(messages.keyColumnHeader)}</th>
				<th css={[styles.thBase, styles.thType]}>{formatMessage(messages.typeColumnHeader)}</th>
				<th css={[styles.thBase, styles.thLead]}>{formatMessage(messages.leadColumnHeader)}</th>
				<th css={[styles.thBase, styles.thCategory]}>{formatMessage(messages.categoryColumnHeader)}</th>
				<th css={[styles.thBase, styles.thSpaceUrl]}>{formatMessage(messages.spaceUrlColumnHeader)}</th>
			</tr>
		</thead>
	);
};

type TableFooterProps = {
	/** Number of results currently shown. */
	shown: number;
	/** Total number of results. Renders as "X+" when total exceeds shown. */
	total: number;
};

const TableFooter = ({ shown, total }: TableFooterProps) => {
	// "25 of 50", or "50 of 50+" in the case that we received more results than we can show the user
	const totalLabel = total > shown ? `${total.toLocaleString()}+` : total.toLocaleString();

	return (
		<div css={styles.footer}>
			{shown} of {totalLabel}
		</div>
	);
};

type SearchResultsLiteProjectTableProps = PropsWithChildren<{
	/** Number of rows currently rendered. */
	shownCount: number;
	/** Total number of matching results. */
	totalCount: number;
	/** Whether or not the project table is the only table rendering in the results page (no project table) */
	isOnlyTableShown: boolean;
}>;

export const SearchResultsLiteProjectTable = ({
	children,
	shownCount,
	totalCount,
	isOnlyTableShown,
}: SearchResultsLiteProjectTableProps): JSX.Element => (
	<div
		css={[
			styles.tableWrapper,
			isOnlyTableShown ? styles.tableWrapperFullHeight : styles.tableWrapperRestrictedHeight,
		]}
	>
		<div css={styles.tableScrollWrapper}>
			<table css={styles.table} data-testid="search-results-lite-project-table">
				<TableHeader />
				<tbody css={styles.tbody}>{children}</tbody>
			</table>
		</div>
		{shownCount > 1 && <TableFooter shown={shownCount} total={totalCount} />}
	</div>
);
